import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Button,
  Fade
} from '@mui/material';
import { 
  AutoAwesome as SparkleIcon,
  Cancel as CancelIcon 
} from '@mui/icons-material';
import { brandColors } from '../../theme/theme';

/**
 * AnalysisLoadingScreen Component
 * 
 * Creative loading experience for 1-2 minute AI analysis.
 * Shows animated progress with rotating status messages.
 */

// Status messages with timing (cumulative seconds)
const LOADING_STAGES = [
  { seconds: 0, message: '📖 Reading your logline...', progress: 10 },
  { seconds: 10, message: '🎬 Analyzing story structure...', progress: 20 },
  { seconds: 20, message: '🎯 Evaluating concept originality...', progress: 35 },
  { seconds: 35, message: '🎭 Identifying comparable films...', progress: 50 },
  { seconds: 50, message: '📊 Assessing market potential...', progress: 65 },
  { seconds: 70, message: '💡 Generating recommendations...', progress: 80 },
  { seconds: 90, message: '✨ Finalizing your analysis...', progress: 95 }
];

const AnalysisLoadingScreen = ({ onCancel }) => {
  const [currentStage, setCurrentStage] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showSlowWarning, setShowSlowWarning] = useState(false);

  useEffect(() => {
    // Update elapsed time every second
    const timer = setInterval(() => {
      setElapsedTime((prev) => {
        const newTime = prev + 1;
        
        // Show warning if taking longer than expected (2 minutes)
        if (newTime >= 120 && !showSlowWarning) {
          setShowSlowWarning(true);
        }
        
        // Update stage based on elapsed time
        const stageIndex = LOADING_STAGES.findIndex(
          (stage, index) => {
            const nextStage = LOADING_STAGES[index + 1];
            return newTime >= stage.seconds && (!nextStage || newTime < nextStage.seconds);
          }
        );
        
        if (stageIndex !== -1 && stageIndex !== currentStage) {
          setCurrentStage(stageIndex);
        }
        
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [currentStage, showSlowWarning]);

  const currentStageData = LOADING_STAGES[currentStage];
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        p: 3
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 600,
          width: '100%',
          p: 5,
          textAlign: 'center',
          borderTop: `4px solid ${brandColors.secondary}`
        }}
      >
        {/* Animated Icon */}
        <Box
          sx={{
            display: 'inline-flex',
            mb: 3,
            animation: 'pulse 2s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': {
                transform: 'scale(1)',
                opacity: 1
              },
              '50%': {
                transform: 'scale(1.1)',
                opacity: 0.8
              }
            }
          }}
        >
          <SparkleIcon sx={{ fontSize: 60, color: brandColors.secondary }} />
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            color: brandColors.primary,
            fontWeight: 600,
            mb: 1
          }}
        >
          Analyzing Your Logline
        </Typography>

        {/* Status Message */}
        <Fade in={true} key={currentStage}>
          <Typography
            variant="h6"
            sx={{
              color: brandColors.secondary,
              fontWeight: 500,
              mb: 3,
              minHeight: '2em'
            }}
          >
            {currentStageData.message}
          </Typography>
        </Fade>

        {/* Progress Bar */}
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={currentStageData.progress}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: `${brandColors.primary}20`,
              '& .MuiLinearProgress-bar': {
                backgroundColor: brandColors.secondary,
                borderRadius: 5
              }
            }}
          />
        </Box>

        {/* Progress Text */}
        <Typography
          variant="body2"
          sx={{
            color: brandColors.primary,
            opacity: 0.7,
            mb: 3
          }}
        >
          {currentStageData.progress}% Complete • {formatTime(elapsedTime)} elapsed
        </Typography>

        {/* Info Text */}
        <Typography
          variant="body2"
          sx={{
            color: brandColors.primary,
            opacity: 0.6,
            mb: 3,
            fontStyle: 'italic'
          }}
        >
          Our AI is conducting a comprehensive analysis including story structure, 
          market comparables, and strategic recommendations. This typically takes 1-2 minutes.
        </Typography>

        {/* Slow Warning */}
        {showSlowWarning && (
          <Fade in={showSlowWarning}>
            <Box
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: 'rgba(245, 124, 0, 0.1)',
                borderRadius: 2,
                border: '1px solid rgba(245, 124, 0, 0.3)'
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#f57c00',
                  fontWeight: 500
                }}
              >
                ⏱️ Analysis is taking longer than expected. 
                This may indicate high server load. Please wait or try again later.
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Cancel Button */}
        {onCancel && (
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={onCancel}
            sx={{
              color: brandColors.primary,
              borderColor: brandColors.primary,
              '&:hover': {
                borderColor: brandColors.secondary,
                backgroundColor: 'rgba(255, 68, 73, 0.05)'
              }
            }}
          >
            Cancel Analysis
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default AnalysisLoadingScreen;

